/**
 * サービス一覧のソート選択肢定義
 *
 * ソートキー × 並び順の「提供する組み合わせ」の正本。
 * UI（SortOptions）・永続化ストア（viewSettings の migrate / setSort）が
 * ともにこの定義を参照するため、選択肢を増減する場合はこのファイルだけを変更する。
 *
 * @module sortOptions
 */

import type { SortBy, SortOrder } from "../types";

export interface SortOption {
  sortBy: SortBy;
  sortOrder: SortOrder;
  labelKey: string;
}

export interface SortSelection {
  sortBy: SortBy;
  sortOrder: SortOrder;
}

/**
 * ドロップダウンに表示する順で定義する。
 * sortBy × sortOrder の直交組み合わせ（6 通り）のうち、実用的な 4 通りのみを提供する。
 */
export const SORT_OPTIONS: readonly SortOption[] = [
  { sortBy: "favorite", sortOrder: "desc", labelKey: "ui.sortFavoriteDesc" },
  { sortBy: "created", sortOrder: "desc", labelKey: "ui.sortCreatedDesc" },
  { sortBy: "name", sortOrder: "asc", labelKey: "ui.sortNameAsc" },
  { sortBy: "name", sortOrder: "desc", labelKey: "ui.sortNameDesc" },
];

export const DEFAULT_SORT_OPTION: SortOption = SORT_OPTIONS[0];

export const findSortOption = (sortBy: unknown, sortOrder: unknown): SortOption | undefined =>
  SORT_OPTIONS.find((option) => option.sortBy === sortBy && option.sortOrder === sortOrder);

/**
 * 任意の値を提供中の組み合わせへ寄せる。
 * 廃止した組み合わせ（favorite × asc など）を永続データや外部入力から受け取っても、
 * 同じソートキーの既定方向へフォールバックする。キー自体が未知ならデフォルトを返す。
 */
export const normalizeSortSelection = (sortBy: unknown, sortOrder: unknown): SortSelection => {
  const exactMatch = findSortOption(sortBy, sortOrder);
  if (exactMatch) {
    return { sortBy: exactMatch.sortBy, sortOrder: exactMatch.sortOrder };
  }

  const sameKeyOption = SORT_OPTIONS.find((option) => option.sortBy === sortBy);
  const fallback = sameKeyOption ?? DEFAULT_SORT_OPTION;
  return { sortBy: fallback.sortBy, sortOrder: fallback.sortOrder };
};
